import React, {useCallback, useEffect, useState} from 'react';
import {Link, useLocation, useParams} from 'react-router-dom';
import TitleMain from "../../components/Title/TitleMain";
import {useModal} from "../../contexts/ModalContext";
import {
    getActiviteApi,
    getActiviteApiWithSeances,
    getActiviteLocal,
    getLabelJourSemaine
} from "../../services/ActivitesService";
import img from "../../assets/images/logo192.png";
import BlocLocation from "../../components/Bloc/BlocLocation";
import styles from "../Seances/SeancePage.module.css";
import ListKeyValue from "../../components/List/ListKeyValue";
import Planning from "../../components/Planning/Planning";
import {getSeancesByActiviteLocal, getSeancesByActiviteApi} from "../../services/SeanceService";
import {getSeancesPlanningApi, getSeancesPlanningLocal} from "../../services/PlanningService";
import TitleSecondary from "../../components/Title/TitleSecondary";
import {useUser} from "../../contexts/UserContext";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCableCar,
    faCalendarAlt,
    faCalendarDay,
    faClockRotateLeft,
    faPersonRunning
} from "@fortawesome/free-solid-svg-icons";
import {useBreadcrumb} from "../../contexts/BreadcrumbContext";
import {ImageIllustration} from "../../components/Image/ImageIllustration";
import Routing from "../../services/routing";
import DateService from "../../services/DateService";


const PLANNING_LEGEND = [
    { label: "Séances disponibles", color: "#7A9A01", planningLegend: 0 },
    { label: "Mes séances à venir", color: "#004851", planningLegend: 1 },
    { label: "Séances en attente", color: "#dab70b", planningLegend: 2 },
    { label: "Séances disponibles prochainement", color: "#da310b", planningLegend: 3 },
];

function ActivitePage() {
    const { id } = useParams();
    const [activite, setActivite] = useState(null);
    const [initialRating, setInitialRating] = useState(0);
    const { user } = useUser();
    const { showModal } = useModal();
    const [planningData, setPlanningData] = useState({ events: [], legend: [] });
    const { updateBreadcrumb } = useBreadcrumb();
    const location = useLocation();

    const handleSuccess = useCallback((message = "Note enregistrée avec succès") => {
        showModal('success', { children: message, timeout: 5000 });
    }, [showModal]);

    const handleError = useCallback((message = "Une erreur est survenue") => {
        showModal('error', { children: message, timeout: 5000 });
    }, [showModal]);

    useEffect(() => {
        if (!user || !id) return;

        const processSeances = (seances) => seances.map(seance => ({
            ...seance,
            startDate: new Date(seance.heure_debut),
            endDate: new Date(seance.heure_fin),
            link: Routing.generate({route: Routing.routes.seance, params: { id: seance.id }}),

        date: seance.heure_debut,
            type: seance.userRegistered ? 2 : 1,
            style: { backgroundColor: new Date(seance.heure_debut) < new Date() ? "#b1b1b1" : seance.userRegistered ? seance.file_attente ? "#dab70b" : "#004851" : "#7A9A01" }
        }));

        const fetchActivite = async () => {
            try {
                const data = await getActiviteApiWithSeances(+id);
                if (!data.activite){
                    process.env.REACT_APP_MODE === "dev" && console.error('error: ', data);
                    handleError(data.message);
                }

                if (data && data.activite) {
                    setActivite(data.activite);
                    updateBreadcrumb([{ name: data.activite.type.nom, path: location.pathname }]);
                    setPlanningData({ legend: PLANNING_LEGEND, events: processSeances(data.activite.seances) });
                }
            } catch (err) {
                handleError("Une erreur est survenue lors de la récupération de l'activité: " + err.message);
                console.error(err);
            }
        };

        fetchActivite();
    }, [id, user]);

    if (!activite) return (<div className="content"><TitleMain text={"Activité non trouvée"}/><Link to={user ? '/' : '/login'}>Retour à l'accueil</Link></div>);

    const listKVData = [
        {"key": "Coach", "value": activite.coach.prenom},
        {"key": "Date", "value": DateService.formatDate(activite.date_start) + " au " + DateService.formatDate(activite.date_end)},
    ];

    const listKVSeancesData = [];
    if (activite.seances && activite.seances.length > 0) {
        for (let i = 0; i < activite.seances.length; i++) {
            listKVSeancesData.push({"key": "Séance "+(i+1), "value": DateService.formatDate(activite.seances[i].heure_debut) + " " + DateService.formatDate(activite.seances[i].heure_fin)});
        }
    }

    const joursList = getLabelJourSemaine(activite.jour_semaines);

    return (
        <div className="content">
            <TitleMain text={activite.type.nom}/>

            <ImageIllustration src={activite.type.image} alt={activite.title} />

            <div style={{ marginTop: "16px" }}>
                <BlocLocation lieu={activite.lieu}/>
            </div>

            <p className={styles.actDescription}>{activite.type.description}</p>

            <div className={styles.seance_info_container}>
                <ul>
                    <li>
                        <FontAwesomeIcon icon={faPersonRunning}/>
                        <span className={styles.seance_info_label}>Coach:</span>
                        <span className={styles.seance_info_value}>{activite.coach.prenom}</span>
                    </li>

                    <li>
                        <FontAwesomeIcon icon={faCalendarAlt}/>
                        <span className={styles.seance_info_label}>Date: </span>
                        <span className={styles.seance_info_value}>Du {DateService.formatFrenchDateWithoutTime(activite.date_start)} au {DateService.formatFrenchDateWithoutTime(activite.date_end)}</span>
                    </li>

                    <li>
                        <FontAwesomeIcon icon={faCalendarDay}/>
                        <span className={styles.seance_info_label}>Créneaux: </span>
                        <span className={styles.seance_info_value}>
                          {joursList.length > 0 ? (
                              <div style={{display: "flex", flexDirection: "column"}}>
                                  {joursList.map((jour, index) => (
                                      <span key={index}>{jour}</span>
                                  ))}
                              </div>
                          ) : (
                              <span>Pas de créneaux</span>
                          )}
                        </span>
                    </li>
                </ul>
            </div>

            <div className={"planningContainer"}>
                <TitleSecondary text={"Les séances"}/>

                <Planning events={planningData.events} height={"250px"} legend={planningData.legend}/>
            </div>
        </div>
    )
}

export default ActivitePage;
